// client/src/components/shared/form/PostalCodeInput.js
// Fältet "123 45" — formaterad postnummer-input med validering och
// inline suggestions-lista. Endast presentation, ingen API-koppling.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import './PostalCodeInput.css'; 

const PostalCodeInput = ({
  value = '',
  onChange,
  onValidationChange,
  suggestions = [],
  onSelectSuggestion,
  label = 'Postal Code',
  required = false,
  placeholder = '123 45'
}) => {
  // Referens till input-element
  const inputRef = useRef(null);
  
  // State för att hantera fokus, validering och val av förslag
  const [isFocused, setIsFocused] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  // Lokal state för att hantera formaterat värde
  const [displayValue, setDisplayValue] = useState('');
  
  // Referens till förslagslistan för att säkerställa korrekt scrollning
  const suggestionsRef = useRef(null);
  
  // Uppdatera displayValue när value från props ändras
  useEffect(() => {
    setDisplayValue(formatPostalCode(value));
  }, [value]);
  
  // Validera postnummer baserat på svenskt format (123 45 eller 12345)
  const validatePostalCode = useCallback((postalCode) => {
    if (!postalCode && !required) {
      return { isValid: true, errorMessage: '' };
    }

    if (!postalCode && required) {
      return { isValid: false, errorMessage: 'Postnummer är obligatoriskt' };
    }

    // Ta bort mellanslag för validering
    const cleanedCode = postalCode.replace(/\s+/g, '');
    
    // Kontrollera att det består av 5 siffror
    if (!/^\d{5}$/.test(cleanedCode)) {
      return { 
        isValid: false, 
        errorMessage: 'Postnummer ska bestå av 5 siffror, t.ex. 123 45' 
      };
    }
    
    return { isValid: true, errorMessage: '' };
  }, [required]);
  
  // Formatera postnummer till 123 45-format
  const formatPostalCode = (code) => {
    if (!code) return '';
    
    // Ta bort alla icke-siffror
    const cleaned = code.replace(/\D/g, '');
    
    // Formatera som 123 45 om vi har 5 siffror
    if (cleaned.length === 5) {
      return `${cleaned.substring(0, 3)} ${cleaned.substring(3, 5)}`;
    }
    
    return cleaned;
  };
  
  // KRITISK FIX: Trigger validation när komponenten mountar och när value ändras
  useEffect(() => {
    if (value !== undefined) {
      const validation = validatePostalCode(value);
      setIsValid(validation.isValid);
      setErrorMessage(validation.errorMessage);
      
      if (onValidationChange) {
        onValidationChange(validation.isValid, validation.errorMessage);
      }
    }
  }, [value, required, onValidationChange, validatePostalCode]);
  
  // Hantera inmatning
  const handleChange = (e) => {
    const newValue = e.target.value;
    
    // Rensa värdet från formatering för att behålla endast siffror
    const cleanedValue = newValue.replace(/\D/g, '');
    
    // Formatera värdet för visning
    const formattedValue = formatPostalCode(cleanedValue);
    
    // Uppdatera lokal state för visning
    setDisplayValue(formattedValue);
    
    // Anropa callback med det rensade värdet
    if (onChange) {
      onChange(cleanedValue);
    }
    
    // Validera och uppdatera tillstånd
    const validation = validatePostalCode(cleanedValue);
    setIsValid(validation.isValid);
    setErrorMessage(validation.errorMessage);
    
    // Informera parent om validering
    if (onValidationChange) {
      onValidationChange(validation.isValid, validation.errorMessage);
    }
    
    // Visa förslag om vi har några och är i fokus
    setShowSuggestions(isFocused && suggestions.length > 0);
    
    // Återställ aktiv förslag-index
    setActiveSuggestionIndex(-1);
  };
  
  // Hantera tangentnavigering i förslag
  const handleKeyDown = (e) => {
    // Bara hantera tangenttryck om det finns förslag
    if (!suggestions.length || !showSuggestions) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveSuggestionIndex(prevIndex => 
          prevIndex < suggestions.length - 1 ? prevIndex + 1 : prevIndex
        );
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        setActiveSuggestionIndex(prevIndex => 
          prevIndex > 0 ? prevIndex - 1 : -1
        );
        break;
        
      case 'Enter':
        e.preventDefault();
        if (activeSuggestionIndex >= 0 && activeSuggestionIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[activeSuggestionIndex]);
        }
        break;
        
      case 'Escape':
        e.preventDefault();
        setShowSuggestions(false);
        break;
        
      default:
        break;
    }
  };
  
  // Hantera val av förslag
  const handleSelectSuggestion = (suggestion) => {
    // Formatera den valda koden
    const formattedCode = formatPostalCode(suggestion.code);
    // Uppdatera visningsvärdet
    setDisplayValue(formattedCode);
    
    if (onSelectSuggestion) {
      onSelectSuggestion(suggestion);
    } else if (onChange) {
      onChange(suggestion.code);
    }
    
    // Dölj förslag efter val
    setShowSuggestions(false);
    
    // Fokusera på input igen
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  
  // Håll aktiv förslag synlig vid piltangentnavigering
  useEffect(() => {
    if (suggestionsRef.current && activeSuggestionIndex >= 0) {
      const activeElement = suggestionsRef.current.children[activeSuggestionIndex];
      if (activeElement) {
        activeElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [activeSuggestionIndex]);
  
  return (
    <div className="postal-code-input">
      {label && (
        <label 
          htmlFor="postal-code-input" 
          className={`postal-code-label ${required ? 'required' : ''}`}
        >
          {label}
          {required && <span className="required-indicator">*</span>}
        </label>
      )}
      
      <div className="postal-code-input-container">
        <input
          ref={inputRef}
          id="postal-code-input"
          type="text"
          value={displayValue} // Använd formaterat värde för visning
          onChange={handleChange}
          onFocus={() => {
            setIsFocused(true);
            setShowSuggestions(suggestions.length > 0);
          }}
          onBlur={() => {
            // Fördröj så att klick på förslag hinner registreras
            setTimeout(() => {
              setIsFocused(false);
              setShowSuggestions(false);
            }, 150);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`postal-code-input-field ${!isValid ? 'invalid' : ''}`}
          aria-invalid={!isValid}
          aria-required={required}
          aria-describedby={!isValid ? 'postal-code-error' : undefined}
        />
        
        {/* Förslag */}
        {showSuggestions && suggestions.length > 0 && (
          <div 
            ref={suggestionsRef}
            className="postal-code-suggestions"
            role="listbox"
            aria-label="Postnummerförslag"
          >
            {suggestions.map((suggestion, index) => (
              <div 
                key={index}
                className={`postal-code-suggestion-item ${index === activeSuggestionIndex ? 'active' : ''}`}
                role="option"
                aria-selected={index === activeSuggestionIndex}
                onClick={() => handleSelectSuggestion(suggestion)}
                onMouseEnter={() => setActiveSuggestionIndex(index)}
              >
                <span className="postal-code-suggestion-code">{formatPostalCode(suggestion.code)}</span>
                <span className="postal-code-suggestion-city">{suggestion.city}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Felmeddelande */}
      {!isValid && errorMessage && (
        <div 
          id="postal-code-error" 
          className="postal-code-error-message"
          role="alert"
        >
          {errorMessage}
        </div>
      )}
    </div>
  );
};

PostalCodeInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      city: PropTypes.string
    })
  ),
  onSelectSuggestion: PropTypes.func,
  label: PropTypes.string,
  required: PropTypes.bool,
  placeholder: PropTypes.string
};

export default PostalCodeInput;